/**
 * Setting a score up: what is in it, in what order, on how many staves, in what metre.
 *
 * Everything here answers with commands rather than with a changed score, so that setting
 * a score up is undoable in the same history as typing a note into it. A refusal is a
 * string, not a throw: asking to put a part somewhere it cannot go is an ordinary thing
 * for a user interface to do, and the answer belongs on the screen.
 *
 * Two operations -- changing what a part is played on, and changing how many staves it has
 * -- cannot be expressed as a property edit, because no command reaches a part's staves.
 * They are done by lifting the part out and putting it back rebuilt, keeping its id so
 * that spanners, directions and anything the caller has selected still point at it, and
 * keeping the groups it was bracketed into.
 */

import { createPart, rest, voice } from "../document/create";
import { plain } from "../document/duration";
import { createId } from "../document/ids";
import type { MeasureId, PartId } from "../document/ids";
import type {
  Clef,
  KeyPrinting,
  KeySignature,
  Part,
  PartMeasure,
  Score,
  Tempo,
  TimeSignature,
  Voice,
} from "../document/types";
import type { Command } from "../commands/types";
import { withoutDoublings } from "./doubling";
import type { InstrumentPreset } from "./instruments";

/** A new part built from a preset, ready for {@link addPart}. */
export function partFor(
  score: Score,
  preset: InstrumentPreset,
  name = preset.name,
  abbreviation = preset.abbreviation,
): Part {
  return createPart(
    name,
    score.measures.map((measure) => measure.id),
    {
      abbreviation,
      clefs: preset.clefs,
      staffCount: preset.clefs.length,
      instrument: {
        name: preset.name,
        sound: preset.sound,
        midiProgram: preset.midiProgram,
        transposition: preset.transposition,
        percussion: preset.percussion ?? null,
        tuning: preset.tuning ?? null,
        keyPrinting: "derived",
      },
      lines: preset.lines,
    },
  );
}

/** Put a part into the score, at `index` or at the bottom of the system. */
export function addPart(score: Score, preset: InstrumentPreset, index?: number): Command {
  return { kind: "insertPart", index: index ?? score.parts.length, part: partFor(score, preset) };
}

/** Take a part out, with it the groups it was in. */
export function removePart(score: Score, id: PartId): Command | string {
  if (!score.parts.some((part) => part.id === id)) return "there is no such part";
  return { kind: "removePart", id };
}

/**
 * Move a part to another place in the system.
 *
 * Lifting it out and putting it back is the only way the command layer reorders parts, and
 * the restore carries the groups as they stand, so a part that is moved inside its bracket
 * stays bracketed.
 */
export function movePart(score: Score, id: PartId, to: number): Command | string {
  const index = score.parts.findIndex((part) => part.id === id);
  const part = score.parts[index];
  if (part === undefined) return "there is no such part";
  const target = Math.max(0, Math.min(to, score.parts.length - 1));
  if (target === index) return "that part is already there";
  return {
    kind: "batch",
    label: "move part",
    commands: [
      { kind: "removePart", id },
      { kind: "restorePart", index: target, part, groups: score.groups },
    ],
  };
}

/** Rename a part, and its short name in the margin of later systems with it. */
export function renamePart(
  score: Score,
  id: PartId,
  name: string,
  abbreviation?: string,
): Command | string {
  if (name.trim() === "") return "a part needs a name";
  if (!score.parts.some((part) => part.id === id)) return "there is no such part";
  return {
    kind: "setPart",
    id,
    props: { name, ...(abbreviation === undefined ? {} : { abbreviation }) },
  };
}

/**
 * A part's bars, refitted to `staffCount` staves.
 *
 * Music already written is kept. A voice on a staff that no longer exists moves to the
 * lowest remaining one rather than being thrown away, and a staff that ends up with
 * nothing on it is given a bar's rest, because a staff with no voice at all in a bar is
 * drawn as a gap. A note drawn across onto a staff that is gone is drawn on its own voice's
 * staff again, since there is no longer anywhere to reach.
 */
function refit(part: Part, staffCount: number): Readonly<Record<MeasureId, PartMeasure>> {
  const measures: Record<MeasureId, PartMeasure> = {};
  for (const [id, measure] of Object.entries(part.measures)) {
    const voices: Voice[] = measure.voices.map((existing) => {
      const staff = existing.staff < staffCount ? existing.staff : staffCount - 1;
      const events = existing.events.map((event) =>
        event.staff !== null && event.staff >= staffCount ? { ...event, staff: null } : event,
      );
      return { ...existing, staff, events };
    });
    for (let staff = 0; staff < staffCount; staff += 1) {
      if (voices.some((line) => line.staff === staff)) continue;
      voices.push(voice(staff, [rest(plain("whole"), true)]));
    }
    const clefChanges = Object.fromEntries(
      Object.entries(measure.clefChanges).filter(([staff]) => Number(staff) < staffCount),
    );
    measures[id as MeasureId] = {
      ...measure,
      voices: voices.sort((a, b) => a.staff - b.staff),
      clefChanges,
    };
  }
  return measures;
}

/** The same part, rebuilt around new staves, keeping its id and its music. */
function rebuilt(part: Part, clefs: readonly Clef[], lines?: number): Part {
  const staves = clefs.map((clef, index) => ({
    id: part.staves[index]?.id ?? createId("staff"),
    clef,
    lines: lines ?? part.staves[index]?.lines ?? 5,
  }));
  return { ...part, staves, measures: refit(part, staves.length) };
}

/** Lift a part out and put it back rebuilt, in place, in its groups. */
function replacing(score: Score, part: Part, next: Part): Command {
  const index = score.parts.findIndex((existing) => existing.id === part.id);
  return {
    kind: "batch",
    label: "change part",
    commands: [
      { kind: "removePart", id: part.id },
      { kind: "restorePart", index, part: next, groups: score.groups },
    ],
  };
}

/**
 * Change what a part is played on.
 *
 * The clef and the number of staves come with the instrument: a violin part changed to a
 * piano part gains its second staff, and the notes already written stay exactly where they
 * are, which for a transposing instrument means they now sound somewhere else. That is the
 * honest reading -- the notes on the page are the notes on the page -- and re-spelling them
 * for the new instrument is a separate thing a caller can ask for.
 */
export function changeInstrument(
  score: Score,
  id: PartId,
  preset: InstrumentPreset,
): Command | string {
  const part = score.parts.find((existing) => existing.id === id);
  if (part === undefined) return "there is no such part";
  const played = rebuilt(
    {
      ...part,
      instrument: {
        name: preset.name,
        sound: preset.sound,
        midiProgram: preset.midiProgram,
        transposition: preset.transposition,
        percussion: preset.percussion ?? null,
        tuning: preset.tuning ?? null,
        // Changing the instrument keeps what the part says about its own signature: a horn
        // part written with none is still written with none when it becomes a trumpet.
        keyPrinting: part.instrument.keyPrinting,
      },
    },
    preset.clefs,
    preset.lines,
  );
  // A kit and a tuning belong to the part for the whole piece, so a part rebuilt as a drum
  // or a tab part puts down whatever its player had been picking up while it was neither.
  const staffBound = preset.percussion !== undefined || preset.tuning !== undefined;
  return replacing(score, part, staffBound ? withoutDoublings(played) : played);
}

/**
 * Change where a part's printed key signature comes from.
 *
 * Most parts print the piece's key moved by their own transposition and want nothing said
 * about it. The setting is for the two parts that do: a horn written with no signature at
 * all, every accidental spelled on the note, and a part spelled the other way round the
 * circle from everyone else -- a harp in F sharp under a score in G flat. Neither changes
 * what the part sounds; both change what its player reads.
 */
export function setKeyPrinting(score: Score, id: PartId, printing: KeyPrinting): Command | string {
  const part = score.parts.find((existing) => existing.id === id);
  if (part === undefined) return "there is no such part";
  if (part.instrument.keyPrinting === printing) return "that part already prints it that way";
  return { kind: "setPart", id, props: { instrument: { keyPrinting: printing } } };
}

/** Give a part more or fewer staves, keeping what is written on the ones it keeps. */
export function setStaffCount(score: Score, id: PartId, count: number): Command | string {
  const part = score.parts.find((existing) => existing.id === id);
  if (part === undefined) return "there is no such part";
  if (count < 1 || !Number.isInteger(count)) return "a part needs at least one staff";
  if (count === part.staves.length) return "that part already has that many staves";
  const clefs = [...Array(count).keys()].map(
    (index) => part.staves[index]?.clef ?? { sign: "F" as const, line: 4, octaveChange: 0 },
  );
  return replacing(score, part, rebuilt(part, clefs));
}

/** Change the clef a staff starts the piece in. */
export function setStartClef(
  score: Score,
  id: PartId,
  staff: number,
  clef: Clef,
): Command | string {
  const part = score.parts.find((existing) => existing.id === id);
  if (part === undefined) return "there is no such part";
  if (part.staves[staff] === undefined) return "that part has no such staff";
  const clefs = part.staves.map((existing, index) => (index === staff ? clef : existing.clef));
  return replacing(score, part, rebuilt(part, clefs));
}

/** State a key signature at a bar. It holds from there until another bar states one. */
export function setKeyAt(score: Score, id: MeasureId, key: KeySignature | null): Command | string {
  if (!score.measures.some((measure) => measure.id === id)) return "there is no such bar";
  return { kind: "setMeasure", id, props: { key } };
}

/**
 * State a metre at a bar.
 *
 * Nothing is re-barred: the bars that follow hold whatever they held, which under a new
 * metre may now be too much or too little. That shows up as a validation problem rather
 * than as a silent rewrite, because deciding where the notes should go instead is an
 * editing decision and not this function's to make.
 */
export function setTimeAt(
  score: Score,
  id: MeasureId,
  time: TimeSignature | null,
): Command | string {
  if (!score.measures.some((measure) => measure.id === id)) return "there is no such bar";
  return { kind: "setMeasure", id, props: { time } };
}

/** Add `count` silent bars, at `index` or at the end of the piece. */
export function addMeasures(score: Score, count: number, index?: number): Command | string {
  if (count < 1 || !Number.isInteger(count)) return "that is not a number of bars";
  const at = index ?? score.measures.length;
  return {
    kind: "batch",
    label: count === 1 ? "add bar" : `add ${count} bars`,
    commands: [...Array(count).keys()].map((step) => ({
      kind: "insertMeasure" as const,
      index: at + step,
    })),
  };
}

/** State a tempo at a bar, or take the one it states away. */
export function setTempoAt(score: Score, id: MeasureId, tempo: Tempo | null): Command | string {
  if (!score.measures.some((measure) => measure.id === id)) return "there is no such bar";
  return { kind: "setMeasure", id, props: { tempo } };
}

/**
 * Take `count` bars away, from `index` or from the end of the piece.
 *
 * Whatever was written in them goes with them, in every part; the command's own inverse
 * puts it back, which is what makes shortening a piece an ordinary undoable edit rather
 * than something to warn about.
 */
export function removeMeasures(score: Score, count: number, index?: number): Command | string {
  if (count < 1 || !Number.isInteger(count)) return "that is not a number of bars";
  const at = index ?? score.measures.length - count;
  if (at < 0 || at + count > score.measures.length) return "the piece does not have those bars";
  if (score.measures.length - count < 1) return "a score has to keep at least one bar";
  const going = score.measures.slice(at, at + count);
  return {
    kind: "batch",
    label: count === 1 ? "remove bar" : `remove ${count} bars`,
    commands: going.map((measure) => ({ kind: "removeMeasure" as const, id: measure.id })),
  };
}

export { groupParts, ungroup } from "./setup-groups";
